import {css, cva, cx} from "@panda/css";
import {motion} from "framer-motion";

import {DsTooltip} from "@pg-design/floating-module";
import {InfoIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {getNumberFormat} from "@pg-design/inputs-module";
import {Text} from "@pg-design/text-module";
import {capitalize} from "@pg-mono/nodash";

import {getPriceLabel} from "../../../../../offer/utils/get_price_label";
import {useGetPropertyDetailQuery} from "../../../../../property/api/get_property_detail";
import {IPropertyDetail} from "../../../../../property/types/IPropertyDetail";
import {getCurrency} from "../../../../../utils/get_currency";
import {leadModalZindex} from "../../../../constants/lead_modal_zindex";
import {useLeadRequiredParams} from "../../../../hooks/use_lead_required_params";
import {ILeadModalPriceConversationPrice} from "../../../../types/ILeadModalPriceConversationPrice";

import * as cns from "./LeadModalPriceConversationPropertyInfo.module.css";

type VariantType = "small" | "large";

interface IProps {
    estimatedPrice: number | ILeadModalPriceConversationPrice | null;
    variant: VariantType;
    className?: string;
}

export function LeadModalPriceConversationPropertyInfo(props: IProps) {
    const {estimatedPrice, variant, className} = props;

    const {propertyId} = useLeadRequiredParams("property");
    const {data: propertyDetail} = useGetPropertyDetailQuery(propertyId);

    if (!propertyDetail) {
        return null;
    }

    const priceRevealed = isPriceRevealed(estimatedPrice);
    const currency = getCurrency({currency: propertyDetail.offer.currency});
    const price = getPrice(estimatedPrice, currency);

    return (
        <motion.div className={cx(propertyInfoStyles({variant}), className)} layout transition={{duration: 0.5, ease: [0.4, 0, 0.2, 1]}}>
            <motion.div className={planColumnStyles({variant})} layout transition={{duration: 0.5, ease: [0.4, 0, 0.2, 1]}}>
                <Image
                    width="760px"
                    height="428px"
                    alt="plan"
                    className={planImageStyles({variant})}
                    imageClassName={cns.leadModalPropertyPlanImage}
                    src={propertyDetail.external_plan_url || propertyDetail.plan_image?.p_img_760x428 || undefined}
                />
            </motion.div>
            <motion.div className={priceColumnStyles({variant})} layout transition={{duration: 0.5, ease: [0.4, 0, 0.2, 1]}}>
                <motion.div className={priceInfoStyles({variant})} layout transition={{duration: 0.4, ease: [0.4, 0, 0.2, 1]}}>
                    <Text variant="body_copy_2" as="span" className={priceTitleStyles}>
                        {priceRevealed &&
                            props.estimatedPrice &&
                            typeof props.estimatedPrice !== "number" &&
                            props.estimatedPrice.min !== props.estimatedPrice.max && (
                                <DsTooltip
                                    popover="Prezentowana cena mieszkania to średnia wartość dla wybranej lokalizacji z uwzględnieniem metrażu, liczby pokoi oraz specyfiki mieszkania. Dokładną cenę tej konkretej nieruchomości otrzymasz od dewelopera."
                                    type="success"
                                    zIndex={leadModalZindex}
                                >
                                    {({refs, getReferenceProps}) => (
                                        <InfoIcon size="2" wrapperSize="2" wrapperColor="transparent" ref={refs.setReference} {...getReferenceProps()} />
                                    )}
                                </DsTooltip>
                            )}
                        {getPriceTitle(propertyDetail.offer.type, estimatedPrice, propertyDetail.offer.is_holiday_location, propertyDetail.offer.is_condohotel)}
                    </Text>
                    <Text variant={variant === "small" ? "headline_6" : "headline_2"} as="span" className={priceStyles({revealed: priceRevealed, variant})}>
                        {price}
                    </Text>

                    <motion.div className={priceInfoVendorStyles({variant})} layout transition={{duration: 0.4, ease: [0.4, 0, 0.2, 1]}}>
                        <Image
                            width="80px"
                            height="60px"
                            alt={propertyDetail.offer.vendor.name}
                            src={propertyDetail.offer.vendor.logo?.v_log_80x60 || undefined}
                            className={vendorLogoStyles({variant})}
                            placeholderIconClasName={cns.leadModalPropertyVendorPlaceholder}
                            imageClassName={cns.leadModalPropertyVendorImage}
                        />
                    </motion.div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
}

// Utils
function getPriceTitle(offerType: IPropertyDetail["offer"]["type"], price: IProps["estimatedPrice"], isHolidayLocation: boolean, isCondoHotel: boolean) {
    const priceRevealed = isPriceRevealed(price);

    const title = [getPriceLabel({offerType, isHolidayLocation, isCondoHotel}).toLocaleLowerCase()];

    if (priceRevealed && price && typeof price !== "number" && price.min !== price.max) {
        title.unshift("estymowana");
    }

    return capitalize(title.join(" "));
}

function getPrice(price: IProps["estimatedPrice"], currency: string) {
    const priceRevealed = isPriceRevealed(price);

    if (!priceRevealed || !price) {
        return <span className={priceNumberStyles}>250 000 zł</span>;
    }

    if (typeof price === "number") {
        return (
            <span className={priceNumberStyles}>
                {getNumberFormat(price, true)} {currency}
            </span>
        );
    }

    if (price && price.min === price.max) {
        return (
            <span className={priceNumberStyles}>
                {getNumberFormat(price.min, true)} {currency}
            </span>
        );
    }

    return (
        <>
            <span className={priceNumberStyles}>{getNumberFormat(price.min, true)}</span> -{" "}
            <span className={priceNumberStyles}>{getNumberFormat(price.max, true)} zł</span>
        </>
    );
}

function isPriceRevealed(price: IProps["estimatedPrice"]) {
    return price !== null;
}

// Styles
const propertyInfoStyles = cva({
    base: {
        display: "flex"
    },
    variants: {
        variant: {
            small: {
                gap: 1,
                flexDirection: "row",
                maxHeight: "16rem"
            },
            large: {
                flex: "0 0 437px",
                flexDirection: "column",
                gap: 4
            }
        }
    }
});

const planImageStyles = cva({
    base: {
        height: "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        transition: "all 0.3s ease-in-out"
    },
    variants: {
        variant: {
            small: {
                aspectRatio: "162 / 129"
            },
            large: {
                aspectRatio: "162 / 129"
            }
        }
    }
});

const planColumnStyles = cva({
    base: {
        maxWidth: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden"
    },
    variants: {
        variant: {
            small: {
                flex: "1 1 162px"
            },
            large: {
                flex: "0 1 320px"
            }
        }
    }
});

const priceColumnStyles = cva({
    base: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    },
    variants: {
        variant: {
            small: {
                flex: "1 1 auto"
            },
            large: {
                flex: "1 1 auto"
            }
        }
    }
});

const priceTitleStyles = css({
    display: "flex",
    gap: "0.5rem"
});

const priceInfoStyles = cva({
    base: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
    },
    variants: {
        variant: {
            small: {
                gap: 1
            },
            large: {
                gap: 2
            }
        }
    }
});

const priceNumberStyles = css({
    whiteSpace: "nowrap"
});

const priceInfoVendorStyles = cva({
    base: {},
    variants: {
        variant: {
            small: {
                flex: "0 1 32px"
            },
            large: {
                flex: "0 1 60px"
            }
        }
    }
});

const priceStyles = cva({
    base: {
        textAlign: "center",
        background: "var(--colors-success)",
        py: 0.5,
        px: 1,
        borderRadius: 2,
        userSelect: "none",
        transition: "all 0.3s ease-in-out",
        fontWeight: "700"
    },
    variants: {
        revealed: {
            true: {
                filter: "none"
            },
            false: {
                filter: "blur(7px)"
            }
        },
        variant: {
            small: {
                fontSize: "sm"
            },
            large: {
                fontSize: "lg"
            }
        }
    }
});

const vendorLogoStyles = cva({
    base: {
        transition: "width 0.4s ease-in-out, height 0.4s ease-in-out",
        willChange: "width, height"
    },
    variants: {
        variant: {
            small: {
                width: "40px",
                height: "30px"
            },
            large: {
                width: "80px",
                height: "60px"
            }
        }
    }
});
import {css, cva, cx} from "@panda/css";
import {motion} from "framer-motion";

import {DsTooltip} from "@pg-design/floating-module";
import {InfoIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {getNumberFormat} from "@pg-design/inputs-module";
import {Text} from "@pg-design/text-module";
import {capitalize} from "@pg-mono/nodash";

import {getPriceLabel} from "../../../../../offer/utils/get_price_label";
import {useGetPropertyDetailQuery} from "../../../../../property/api/get_property_detail";
import {IPropertyDetail} from "../../../../../property/types/IPropertyDetail";
import {getCurrency} from "../../../../../utils/get_currency";
import {leadModalZindex} from "../../../../constants/lead_modal_zindex";
import {useLeadRequiredParams} from "../../../../hooks/use_lead_required_params";
import {ILeadModalPriceConversationPrice} from "../../../../types/ILeadModalPriceConversationPrice";

import * as cns from "./LeadModalPriceConversationPropertyInfo.module.css";

type VariantType = "small" | "large";

interface IProps {
    estimatedPrice: number | ILeadModalPriceConversationPrice | null;
    variant: VariantType;
    className?: string;
}

export function LeadModalPriceConversationPropertyInfo(props: IProps) {
    const {estimatedPrice, variant, className} = props;

    const {propertyId} = useLeadRequiredParams("property");
    const {data: propertyDetail} = useGetPropertyDetailQuery(propertyId);

    if (!propertyDetail) {
        return null;
    }

    const priceRevealed = isPriceRevealed(estimatedPrice);
    const currency = getCurrency({currency: propertyDetail.offer.currency});
    const price = getPrice(estimatedPrice, currency);

    return (
        <motion.div className={cx(propertyInfoStyles({variant}), className)} layout transition={{duration: 0.5, ease: [0.4, 0, 0.2, 1]}}>
            <motion.div className={planColumnStyles({variant})} layout transition={{duration: 0.5, ease: [0.4, 0, 0.2, 1]}}>
                <Image
                    width="760px"
                    height="428px"
                    alt="plan"
                    className={planImageStyles({variant})}
                    imageClassName={cns.leadModalPropertyPlanImage}
                    src={propertyDetail.external_plan_url || propertyDetail.plan_image?.p_img_760x428 || undefined}
                />
            </motion.div>
            <motion.div className={priceColumnStyles({variant})} layout transition={{duration: 0.5, ease: [0.4, 0, 0.2, 1]}}>
                <motion.div className={priceInfoStyles({variant})} layout transition={{duration: 0.4, ease: [0.4, 0, 0.2, 1]}}>
                    <Text variant="body_copy_2" as="span" className={priceTitleStyles}>
                        {priceRevealed &&
                            props.estimatedPrice &&
                            typeof props.estimatedPrice !== "number" &&
                            props.estimatedPrice.min !== props.estimatedPrice.max && (
                                <DsTooltip
                                    popover="Prezentowana cena mieszkania to średnia wartość dla wybranej lokalizacji z uwzględnieniem metrażu, liczby pokoi oraz specyfiki mieszkania. Dokładną cenę tej konkretej nieruchomości otrzymasz od dewelopera."
                                    type="success"
                                    zIndex={leadModalZindex}
                                >
                                    {({refs, getReferenceProps}) => (
                                        <InfoIcon size="2" wrapperSize="2" wrapperColor="transparent" ref={refs.setReference} {...getReferenceProps()} />
                                    )}
                                </DsTooltip>
                            )}
                        {getPriceTitle(propertyDetail.offer.type, estimatedPrice, propertyDetail.offer.is_holiday_location, propertyDetail.offer.is_condohotel)}
                    </Text>
                    <Text variant={variant === "small" ? "headline_6" : "headline_2"} as="span" className={priceStyles({revealed: priceRevealed, variant})}>
                        {price}
                    </Text>

                    <motion.div className={priceInfoVendorStyles({variant})} layout transition={{duration: 0.4, ease: [0.4, 0, 0.2, 1]}}>
                        <Image
                            width="80px"
                            height="60px"
                            alt={propertyDetail.offer.vendor.name}
                            src={propertyDetail.offer.vendor.logo?.v_log_80x60 || undefined}
                            className={vendorLogoStyles({variant})}
                            placeholderIconClasName={cns.leadModalPropertyVendorPlaceholder}
                            imageClassName={cns.leadModalPropertyVendorImage}
                        />
                    </motion.div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
}

// Utils
function getPriceTitle(offerType: IPropertyDetail["offer"]["type"], price: IProps["estimatedPrice"], isHolidayLocation: boolean, isCondoHotel: boolean) {
    const priceRevealed = isPriceRevealed(price);

    const title = [getPriceLabel({offerType, isHolidayLocation, isCondoHotel}).toLocaleLowerCase()];

    if (priceRevealed && price && typeof price !== "number" && price.min !== price.max) {
        title.unshift("estymowana");
    }

    return capitalize(title.join(" "));
}

function getPrice(price: IProps["estimatedPrice"], currency: string) {
    const priceRevealed = isPriceRevealed(price);

    if (!priceRevealed || !price) {
        return <span className={priceNumberStyles}>250 000 zł</span>;
    }

    if (typeof price === "number") {
        return (
            <span className={priceNumberStyles}>
                {getNumberFormat(price, true)} {currency}
            </span>
        );
    }

    if (price && price.min === price.max) {
        return (
            <span className={priceNumberStyles}>
                {getNumberFormat(price.min, true)} {currency}
            </span>
        );
    }

    return (
        <>
            <span className={priceNumberStyles}>{getNumberFormat(price.min, true)}</span> -{" "}
            <span className={priceNumberStyles}>{getNumberFormat(price.max, true)} zł</span>
        </>
    );
}

function isPriceRevealed(price: IProps["estimatedPrice"]) {
    return price !== null;
}

// Styles
const propertyInfoStyles = cva({
    base: {
        display: "flex"
    },
    variants: {
        variant: {
            small: {
                gap: 1,
                flexDirection: "row",
                maxHeight: "16rem"
            },
            large: {
                flex: "0 0 437px",
                flexDirection: "column",
                gap: 4
            }
        }
    }
});

const planImageStyles = cva({
    base: {
        height: "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        transition: "all 0.3s ease-in-out"
    },
    variants: {
        variant: {
            small: {
                aspectRatio: "162 / 129"
            },
            large: {
                aspectRatio: "162 / 129"
            }
        }
    }
});

const planColumnStyles = cva({
    base: {
        maxWidth: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden"
    },
    variants: {
        variant: {
            small: {
                flex: "1 1 162px"
            },
            large: {
                flex: "0 1 320px"
            }
        }
    }
});

const priceColumnStyles = cva({
    base: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    },
    variants: {
        variant: {
            small: {
                flex: "1 1 auto"
            },
            large: {
                flex: "1 1 auto"
            }
        }
    }
});

const priceTitleStyles = css({
    display: "flex",
    gap: "0.5rem"
});

const priceInfoStyles = cva({
    base: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
    },
    variants: {
        variant: {
            small: {
                gap: 1
            },
            large: {
                gap: 2
            }
        }
    }
});

const priceNumberStyles = css({
    whiteSpace: "nowrap"
});

const priceInfoVendorStyles = cva({
    base: {},
    variants: {
        variant: {
            small: {
                flex: "0 1 32px"
            },
            large: {
                flex: "0 1 60px"
            }
        }
    }
});

const priceStyles = cva({
    base: {
        textAlign: "center",
        background: "var(--colors-success)",
        py: 0.5,
        px: 1,
        borderRadius: 2,
        userSelect: "none",
        transition: "all 0.3s ease-in-out",
        fontWeight: "700"
    },
    variants: {
        revealed: {
            true: {
                filter: "none"
            },
            false: {
                filter: "blur(7px)"
            }
        },
        variant: {
            small: {
                fontSize: "sm"
            },
            large: {
                fontSize: "lg"
            }
        }
    }
});

const vendorLogoStyles = cva({
    base: {
        transition: "width 0.4s ease-in-out, height 0.4s ease-in-out",
        willChange: "width, height"
    },
    variants: {
        variant: {
            small: {
                width: "40px",
                height: "30px"
            },
            large: {
                width: "80px",
                height: "60px"
            }
        }
    }
});
